import {
  format,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  parse,
  startOfWeek,
  getDay,
} from 'date-fns';
import { de } from 'date-fns/locale';
import React, { useEffect, useRef, useState } from 'react';
import { dateFnsLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { useInView } from 'react-intersection-observer';

import { useLazyQuery, useMutation } from '@lichtblick/apollo';
import { useTranslation } from '@lichtblick/i18next';
import { ArrowBack, ArrowForward } from '@lichtblick/icons';
import { logger } from '@lichtblick/logger';
import { TextVariants } from '@lichtblick/theme';
import {
  AppointmentReducerState,
  DateRange,
  GetAppointment_V1Query,
  GetCalendarItems_V1Query,
  GetAppointment_V1QueryVariables,
  ScheduleAppointment_V1Mutation,
  ScheduleAppointment_V1MutationVariables,
  GetBlocker_V1Query,
  GetBlocker_V1QueryVariables,
  GetCalendarItemsInput,
} from '@lichtblick/types';
import { appointmentToAppointmentForm, blockerToAppointment, getToday } from '@lichtblick/utils';

import { AgendaViewDaily } from './AgendaViewDaily';
import { AppointmentDrawer } from './AppointmentDrawer';
import { appointmentDefaultState } from './AppointmentReducer';
import { CalendarAppointment } from './CalendarAppointment';
import { convertAppointmentToEvent } from './calendarEventUtils';
import { CustomShowMore } from './CustomShowMore';
import { Filters } from './Filters';
import {
  CalendarCellNewButton,
  Controls,
  CalendarDateHeaderContainer,
  CalendarDateHeaderLabel,
  FilterWrapper,
  CustomCalendar,
  CalendarContainer,
  CalendarStylesOverride,
  ScrollableCalendarContainer,
  MonthsContainer,
  MonthContainer,
  HideOnMobile,
  MobileOnly,
  DesktopOnly,
} from './InstallationCalendar.styles';
import { CalendarEvent } from './type';
import { parseEditingValue, resolveCreatingMode, useAppointmentQueryState } from './useAppointmentQueryState';

import { Button, DatePicker, IconButton, MessageDialog, SegmentedButton, SkeletonComponent, Snackbar, Text } from '..';

import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { GET_APPOINTMENT, GET_BLOCKER, SCHEDULE_APPOINTMENT } from '@/graphql/pm/appointment';
import { useCalendarFilters } from '@/hooks/pm/useCalendarFilters';
import { useOptions } from '@/hooks/pm/useOptions';

const locales = { de };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});
const MONTHS_TO_SHOW = 3; // Show current, previous, and next month

const DnDCalendar = withDragAndDrop(CustomCalendar);

const getDayCellProps = (date: Date, visibleMonth: Date) => {
  const classNames: string[] = [];
  const day = getDay(date);
  let shouldHighlightDay = false;

  if (day === 0 || day === 6) {
    classNames.push('calendar-day-weekend');
    shouldHighlightDay = true;
  }

  if (!isSameMonth(date, visibleMonth)) {
    classNames.push('calendar-day-outside-month');
    shouldHighlightDay = true;
  }

  classNames.push(`date-${toDateKey(date)}`);

  return {
    className: classNames.join(' '),
    style: shouldHighlightDay ? { backgroundColor: 'var(--colors-gray-10)' } : undefined,
  };
};

const toDateKey = (date: Date) => format(date, 'yyyy-MM-dd');

export const InstallationCalendar = ({
  appointments,
  isLoading,
  onFilterSubmitCallback,
}: {
  appointments: GetCalendarItems_V1Query['getCalendarItems_V1']['data'];
  isLoading: boolean;
  onFilterSubmitCallback: (overrides?: GetCalendarItemsInput, hasPreviousDate?: boolean) => void;
}) => {
  type PendingCalendarChange = { end: Date; event: CalendarEvent; start: Date };

  const [droppedEvent, setDroppedEvent] = useState<{ end: Date; event: CalendarEvent; start: Date } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggingEventStartDay, setDraggingEventStartDay] = useState<string | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>();
  const [appointmentFormPrefill, setAppointmentFormPrefill] = useState<AppointmentReducerState | undefined>();
  const [isLoadingAppointment, setIsLoadingAppointment] = useState(false);
  const [drawerErrorMessage, setDrawerErrorMessage] = useState<string | undefined>();
  const { t } = useTranslation();
  const {
    calendarFilters,
    clearAll,
    setAppointmentType,
    setBinding,
    setBranch,
    setNotAfter,
    setNotBefore,
    setPartner,
    setPerson,
    setQuery,
    setTeam,
  } = useCalendarFilters();
  const [shouldShowToast, setShouldShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const { appointmentTypeOptions, checkBlocker } = useOptions();
  const { close, isCreating, isEditing, openForCreating, openForCreatingAtDate, openForEditing } =
    useAppointmentQueryState();
  const calendarRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<{ resetForm: () => void }>(null);

  const [visibleMonths, setVisibleMonths] = useState<Date[]>([]);
  const [isLoadingMonths, setIsLoadingMonths] = useState(false);
  const [isScrollView, setIsScrollView] = useState<boolean>(false);
  const [hoveredDateKey, setHoveredDateKey] = useState<string | null>(null);
  const monthRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [selectedAppointment, setSelectedAppointment] = useState<
    GetAppointment_V1Query['getAppointment_V1']['data'] | null
  >(null);

  const [getAppointment] = useLazyQuery<GetAppointment_V1Query, GetAppointment_V1QueryVariables>(GET_APPOINTMENT, {
    fetchPolicy: 'network-only',
  });

  const [getBlocker] = useLazyQuery<GetBlocker_V1Query, GetBlocker_V1QueryVariables>(GET_BLOCKER, {
    fetchPolicy: 'network-only',
  });
  const [scheduleAppointment] = useMutation<ScheduleAppointment_V1Mutation, ScheduleAppointment_V1MutationVariables>(
    SCHEDULE_APPOINTMENT,
  );

  const onCalendarFiltersChanged = (overrides?: GetCalendarItemsInput, hasPreviousDate?: boolean) => {
    setDrawerErrorMessage(undefined);
    close();
    onFilterSubmitCallback(overrides, hasPreviousDate);
  };

  const scrollToDate = (targetDate: Date) => {
    const dayNumber = targetDate.getDate().toString();

    const observer = new MutationObserver(() => {
      const buttons = document.querySelectorAll('.rbc-date-cell button');

      for (const button of buttons) {
        if (button.textContent?.trim() === dayNumber) {
          button.scrollIntoView({ behavior: 'smooth', block: 'center' });
          observer.disconnect();

          return;
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const [lastMonthRef, isLastMonthInView] = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  useEffect(() => {
    if (appointments) {
      const events = convertAppointmentToEvent(appointments);

      setCalendarEvents(events);

      setIsLoadingMonths(false);
    }
  }, [appointments, isLoading]);

  useEffect(() => {
    if (isLastMonthInView && !isLoading) {
      const lastVisibleMonth = visibleMonths[visibleMonths.length - 1];

      const monthsToAdd = Array.from({ length: 3 }, (_, i) => addMonths(lastVisibleMonth, i + 1));

      setVisibleMonths((prev) => [...prev, ...monthsToAdd]);

      setIsLoadingMonths(true);

      const notBeforeUtc = startOfMonth(monthsToAdd[0]);
      const notAfterUtc = endOfMonth(monthsToAdd[monthsToAdd.length - 1]);

      onCalendarFiltersChanged(
        {
          endAtUtc: notAfterUtc,
          startAtUtc: notBeforeUtc,
        },
        true,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLastMonthInView, isLoading]);

  useEffect(() => {
    setTimeout(() => scrollToDate(new Date()), 1000);
  }, []);

  useEffect(() => {
    if (appointments) {
      const events = convertAppointmentToEvent(appointments);

      setCalendarEvents(events);
    }
  }, [appointments]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const fetchAndEditAppointment = async () => {
      setIsLoadingAppointment(true);
      setDrawerErrorMessage(undefined);

      const parsedEditing = parseEditingValue(isEditing);

      if (!parsedEditing.id) {
        setSelectedAppointment(null);
        setAppointmentFormPrefill(undefined);
        setDrawerErrorMessage(t('pm:notFound'));
        setIsLoadingAppointment(false);

        return;
      }

      try {
        let appointmentData: GetAppointment_V1Query['getAppointment_V1']['data'] | null = null;

        if (parsedEditing.isBlocker) {
          const blocker = await getBlocker({
            variables: { input: { blockerId: parseInt(parsedEditing.id ?? '', 10) } },
          });

          appointmentData = blockerToAppointment(blocker.data?.getBlocker_V1.data, appointmentTypeOptions);
        } else {
          const data = await getAppointment({
            variables: {
              input: {
                appointmentId: parseInt(parsedEditing.id ?? '', 10),
                includeBranchOffice: true,
                includePartnerCompanies: true,
              },
            },
          });

          appointmentData = data.data?.getAppointment_V1.data ?? null;
        }

        if (appointmentData) {
          setSelectedAppointment(appointmentData);
          const formPrefill: AppointmentReducerState = appointmentToAppointmentForm(appointmentData);

          setAppointmentFormPrefill(formPrefill);
        } else {
          setSelectedAppointment(null);
          setAppointmentFormPrefill(undefined);
          setDrawerErrorMessage(t('pm:notFound'));
        }
      } finally {
        setIsLoadingAppointment(false);
      }
    };

    fetchAndEditAppointment();
  }, [appointmentTypeOptions, getAppointment, getBlocker, isEditing, t]);

  useEffect(() => {
    if (!isCreating) {
      return;
    }

    const resolveCreatingState = async () => {
      setSelectedAppointment(null);
      setDrawerErrorMessage(undefined);
      drawerRef.current?.resetForm();

      const resolved = resolveCreatingMode(isCreating);

      if (resolved.mode === 'blank') {
        setAppointmentFormPrefill(undefined);

        return;
      }

      if (resolved.mode === 'date') {
        const creatingDate = parse(resolved.date, 'yyyy-MM-dd', new Date());
        const startDate = new Date(creatingDate);
        const endDate = new Date(creatingDate);

        startDate.setHours(8, 0, 0, 0);
        endDate.setHours(17, 0, 0, 0);

        setAppointmentFormPrefill({
          ...appointmentDefaultState,
          endDate,
          people: [...appointmentDefaultState.people],
          startDate,
          teams: [...(appointmentDefaultState.teams ?? [])],
        });

        return;
      }

      if (resolved.mode === 'workOrder') {
        setAppointmentFormPrefill({
          ...appointmentDefaultState,
          people: [...appointmentDefaultState.people],
          teams: [...(appointmentDefaultState.teams ?? [])],
          workOrder: resolved.workOrderId,
        });
      }
    };

    resolveCreatingState();
  }, [isCreating]);

  const handleNavigate = (handle: string) => {
    close();

    const today = new Date();
    let notBeforeUtc = startOfMonth(today);
    let notAfterUtc = endOfMonth(today);

    switch (handle) {
      case 'today': {
        if (!calendarFilters?.notBeforeUtc) {
          break;
        }

        setNotBefore(notBeforeUtc);
        setNotAfter(notAfterUtc);

        break;
      }
      case 'back': {
        if (!calendarFilters?.notBeforeUtc) {
          break;
        }

        const nextMonthDate = subMonths(calendarFilters?.notBeforeUtc, 1);

        notBeforeUtc = startOfMonth(nextMonthDate);
        notAfterUtc = endOfMonth(nextMonthDate);

        setNotBefore(notBeforeUtc);
        setNotAfter(notAfterUtc);
        break;
      }
      case 'forward': {
        if (!calendarFilters?.notBeforeUtc) {
          break;
        }

        const nextMonthDate = addMonths(calendarFilters?.notBeforeUtc, 1);

        notBeforeUtc = startOfMonth(nextMonthDate);
        notAfterUtc = endOfMonth(nextMonthDate);

        setNotBefore(notBeforeUtc);
        setNotAfter(notAfterUtc);

        break;
      }
    }

    // Scroll to the month if in scroll view
    if (isScrollView) {
      const monthKey = format(notBeforeUtc, 'yyyy-MM');
      const monthElement = monthRefs.current[monthKey];

      if (monthElement) {
        setTimeout(() => {
          monthElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
    }

    onCalendarFiltersChanged({
      endAtUtc: notAfterUtc,
      startAtUtc: notBeforeUtc,
    });
  };
  const CustomToolbar = () => {
    return <div />;
  };
  const handleEventClick = async (event: CalendarEvent) => {
    const isBlocker = event.isBlocker ?? checkBlocker(event.type);

    // Open drawer immediately for instant feedback.
    setSelectedAppointment(null);
    setAppointmentFormPrefill(undefined);
    setDrawerErrorMessage(undefined);
    openForEditing(event.id.toString(), isBlocker);
    setIsLoadingAppointment(true);
  };

  const CalendarAppointmentEvent = ({ event }: { event: CalendarEvent }) => {
    return <CalendarAppointment event={event} onEdit={handleEventClick} />;
  };

  const handleCreateAppointmentAtDate = (date: Date) => {
    const startDate = new Date(date);
    const endDate = new Date(date);

    startDate.setHours(8, 0, 0, 0);
    endDate.setHours(17, 0, 0, 0);

    setSelectedAppointment(null);
    setDrawerErrorMessage(undefined);
    drawerRef.current?.resetForm();
    setAppointmentFormPrefill({
      ...appointmentDefaultState,
      endDate,
      people: [...appointmentDefaultState.people],
      startDate,
      teams: [...(appointmentDefaultState.teams ?? [])],
    });
    openForCreatingAtDate(format(date, 'yyyy-MM-dd'));
  };

  const getDateKeyFromDayBackground = (dayBg: Element | null): string | null => {
    if (!dayBg) {
      return null;
    }

    const dateClass = Array.from(dayBg.classList).find((className) => /^date-\d{4}-\d{2}-\d{2}$/.test(className));

    return dateClass ? dateClass.replace('date-', '') : null;
  };

  // Handle hover tracking at the calendar container level
  const handleCalendarPointerMove = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    const directDayBackground = target.closest('.rbc-day-bg');
    const directDateKey = getDateKeyFromDayBackground(directDayBackground);

    if (directDateKey) {
      setHoveredDateKey(directDateKey);

      return;
    }

    const hoveredElement = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    const hoveredDayBackground = hoveredElement?.closest('.rbc-day-bg') ?? null;
    const hoveredDateKey = getDateKeyFromDayBackground(hoveredDayBackground);

    if (hoveredDateKey) {
      setHoveredDateKey(hoveredDateKey);

      return;
    }

    const monthRow = target.closest('.rbc-month-row');

    if (!monthRow) {
      return;
    }

    const rowBackground = monthRow.querySelector('.rbc-row-bg');

    if (!rowBackground) {
      return;
    }

    const dayBackgrounds = Array.from(rowBackground.querySelectorAll('.rbc-day-bg')) as HTMLElement[];

    const dayBackgroundUnderCursor =
      dayBackgrounds.find((dayBackground) => {
        const rect = dayBackground.getBoundingClientRect();

        return e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
      }) || null;

    const dateKey = getDateKeyFromDayBackground(dayBackgroundUnderCursor);

    if (dateKey) {
      setHoveredDateKey(dateKey);
    }
  };

  const handleCalendarPointerLeave = () => {
    setHoveredDateKey(null);
  };

  const CalendarDateHeader = ({ date, label }: { date: Date; label: string }) => {
    const dateKey = toDateKey(date);
    const isVisible = !isDragging && hoveredDateKey === dateKey;

    return (
      <CalendarDateHeaderContainer>
        <CalendarDateHeaderLabel $variant={TextVariants.body.xs}>{label}</CalendarDateHeaderLabel>
        <CalendarCellNewButton
          $isVisible={isVisible}
          className="calendar-cell-new-btn"
          onClick={(event) => {
            event.stopPropagation();
            handleCreateAppointmentAtDate(date);
          }}
          type="button"
        >
          + New
        </CalendarCellNewButton>
      </CalendarDateHeaderContainer>
    );
  };
  const onEventDrop = async () => {
    if (droppedEvent) {
      const { end, event, start } = droppedEvent;
      const updatedEvents = calendarEvents?.map((ev) => (ev.id === event.id ? { ...ev, start, end } : ev));
      const logContext = {
        component: 'CalendarEventHandler',
        operation: 'onEventDrop',
        appointmentId: event?.id,
        start,
        end,
      };

      try {
        const res = await scheduleAppointment({
          variables: {
            input: {
              appointmentId: event.id,
              startAtUtc: start,
              endAtUtc: end,
            },
          },
        });
        const currentError = res.data?.scheduleAppointment_V1.error ?? 'UNKNOWN';

        setDroppedEvent(null);

        if (res.data?.scheduleAppointment_V1.error && res.data?.scheduleAppointment_V1.error === 'INVALID_STATUS') {
          setToastMessage(
            `${t('pm:snackbarCalendar.messageFailedMoved')}. ${t('pm:snackbarCalendar.reason')}: ${t(`pm:snackbarCalendar.errorReason.${currentError}`)}`,
          );
          setShouldShowToast(true);

          return;
        }

        setCalendarEvents(updatedEvents);
        setToastMessage(t('pm:snackbarCalendar:messageSuccessfulMoved'));
        setShouldShowToast(true);
      } catch (error) {
        logger.error('Unexpected error during appointment drop handling', {
          ...logContext,
          error: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
        });
      }
    }
  };

  const handleEventDrop = (change: PendingCalendarChange) => {
    setIsDragging(false);
    setDraggingEventStartDay(null);
    const { end, event, start } = change;
    const isNoOpDrop = isSameDay(start, event.start) && isSameDay(end, event.end);

    if (isNoOpDrop) {
      setDroppedEvent(null);

      return;
    }

    setDroppedEvent(change);
  };

  const handleEventResize = (change: PendingCalendarChange) => {
    setDraggingEventStartDay(null);
    setIsDragging(false);
    const { end, event, start } = change;
    const isNoOpResize = start.getTime() === event.start.getTime() && end.getTime() === event.end.getTime();

    if (isNoOpResize) {
      setDroppedEvent(null);

      return;
    }

    setDroppedEvent(change);
  };

  useEffect(() => {
    const initialMonths = Array.from({ length: MONTHS_TO_SHOW }, (_, i) => addMonths(new Date(), i - 1));

    setVisibleMonths(initialMonths);
  }, []);

  const handleDatePicker = async (date: DateRange) => {
    close();

    const selectedMonth = date?.from;

    setNotBefore(selectedMonth);
    setNotAfter(endOfMonth(selectedMonth || new Date()));

    if (isScrollView && selectedMonth) {
      const monthKey = format(selectedMonth, 'yyyy-MM');
      const monthElement = monthRefs.current[monthKey];

      if (monthElement) {
        setTimeout(() => {
          monthElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      } else {
        const monthsToAdd: Date[] = [];
        let currentMonth = visibleMonths[visibleMonths.length - 1];

        while (
          currentMonth &&
          selectedMonth > currentMonth &&
          monthsToAdd.length < 12 // Safety limit
        ) {
          currentMonth = addMonths(currentMonth, 1);
          monthsToAdd.push(currentMonth);

          if (format(currentMonth, 'yyyy-MM') === monthKey) {
            break;
          }
        }

        if (monthsToAdd.length > 0) {
          setVisibleMonths((prev) => [...prev, ...monthsToAdd]);
          setIsLoadingMonths(true);

          const notBeforeUtc = startOfMonth(monthsToAdd[0]);
          const notAfterUtc = endOfMonth(monthsToAdd[monthsToAdd.length - 1]);

          const scrollToMonth = monthKey;

          onCalendarFiltersChanged(
            {
              endAtUtc: notAfterUtc,
              startAtUtc: notBeforeUtc,
            },
            true,
          );

          setTimeout(() => {
            const targetElement = monthRefs.current[scrollToMonth];

            if (targetElement) {
              targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }
          }, 100);
        }
      }
    }

    if (!isScrollView) {
      onCalendarFiltersChanged();
    }
  };

  return (
    <CalendarContainer ref={calendarRef}>
      <Snackbar
        color="black"
        isOpen={shouldShowToast}
        message={toastMessage}
        setOpen={() => setShouldShowToast(false)}
        title={t('pm:snackbarCalendar.infoTitle')}
      />
      <MessageDialog
        acceptLabel={t('common:yes')}
        cancelLabel={t('common:no')}
        description={t('pm:calendarDndModal', { date: droppedEvent ? droppedEvent.start.toLocaleDateString() : '' })}
        isOpen={!!droppedEvent}
        onAccept={() => onEventDrop()}
        onCancel={() => setDroppedEvent(null)}
        onClose={() => setDroppedEvent(null)}
      />
      <FilterWrapper>
        <Controls>
          <Button
            label={t('today')}
            onClick={() => handleNavigate('today')}
            variant={
              calendarFilters?.notAfterUtc &&
              calendarFilters?.notBeforeUtc &&
              getToday() <= calendarFilters?.notAfterUtc &&
              getToday() >= calendarFilters?.notBeforeUtc
                ? 'primary'
                : 'secondary'
            }
          />
          <IconButton icon={<ArrowBack />} isOutlined onClick={() => handleNavigate('back')} variant="secondary" />
          <IconButton
            icon={<ArrowForward />}
            isOutlined
            onClick={() => handleNavigate('forward')}
            variant="secondary"
          />
          {calendarFilters?.notBeforeUtc && (
            <Text $variant={TextVariants.header.s}>
              {format(calendarFilters?.notBeforeUtc, 'MMMM yyyy', { locale: de })}
            </Text>
          )}
        </Controls>
        <DatePicker
          date={{ from: calendarFilters?.notBeforeUtc, to: calendarFilters?.notAfterUtc }}
          setDate={(date) => {
            if (!date || date instanceof Date) {
              return;
            }

            handleDatePicker(date);
          }}
        />

        <HideOnMobile>
          <SegmentedButton
            defaultValue={isScrollView ? 'multi' : 'single'}
            onOptionChange={(value) => {
              close();

              if (value === 'multi') {
                setIsScrollView(true);
              } else {
                setIsScrollView(false);
              }
            }}
            options={[
              {
                label: t('pm:single'),
                value: 'single',
              },
              {
                label: t('pm:multi'),
                value: 'multi',
              },
            ]}
          />
        </HideOnMobile>
        <Filters
          calendarFilters={calendarFilters}
          clearAll={clearAll}
          onFilterSubmitCallback={onCalendarFiltersChanged}
          setAppointmentType={setAppointmentType}
          setBinding={setBinding}
          setBranch={setBranch}
          setPartner={setPartner}
          setPerson={setPerson}
          setQuery={setQuery}
          setTeam={setTeam}
        />
        <Button
          label={t('pm:newAppointment')}
          onClick={() => {
            setAppointmentFormPrefill(undefined);
            setSelectedAppointment(null);
            drawerRef.current?.resetForm();
            openForCreating();
          }}
          variant="primary"
        />
      </FilterWrapper>
      <MobileOnly>
        {isLoading ? (
          <SkeletonComponent height="100vh" width="100%" />
        ) : (
          <AgendaViewDaily
            endDate={calendarFilters?.notAfterUtc}
            events={calendarEvents || []}
            onCreateAppointment={handleCreateAppointmentAtDate}
            onEventDrop={(event) => handleEventDrop(event as PendingCalendarChange)}
            onSelectEvent={handleEventClick}
            startDate={calendarFilters?.notBeforeUtc}
          />
        )}
      </MobileOnly>
      <DesktopOnly>
        {isScrollView ? (
          <ScrollableCalendarContainer>
            <MonthsContainer>
              {visibleMonths.map((month, index) => {
                const isLastMonth = index === visibleMonths.length - 1;

                return (
                  <MonthContainer
                    key={month.toString()}
                    ref={(el) => {
                      const monthKey = format(month, 'yyyy-MM');

                      monthRefs.current[monthKey] = el;

                      if (isLastMonth) {
                        lastMonthRef(el);
                      }
                    }}
                  >
                    <Text $variant={TextVariants.header.m}>{format(month, 'MMMM yyyy', { locale: de })}</Text>
                    <CalendarStylesOverride
                      $hideDragPreview={
                        isDragging && draggingEventStartDay !== null && hoveredDateKey === draggingEventStartDay
                      }
                      data-visible-month={format(month, 'yyyy-MM-dd')}
                      onPointerLeave={handleCalendarPointerLeave}
                      onPointerMove={handleCalendarPointerMove}
                      onPointerUp={() => {
                        setIsDragging(false);
                        setDraggingEventStartDay(null);
                      }}
                    >
                      {/* @ts-expect-error react-big-calendar types not compatible with React 19 */}
                      <DnDCalendar
                        components={{
                          event: CalendarAppointmentEvent,
                          dateHeader: CalendarDateHeader,
                          toolbar: CustomToolbar,

                          showMore: ({ count, remainingEvents, slotDate }) => (
                            <CustomShowMore
                              count={count}
                              handleEventClick={handleEventClick}
                              remainingEvents={remainingEvents as CalendarEvent[]}
                              slotDate={slotDate}
                            />
                          ),
                        }}
                        date={month}
                        dayPropGetter={(date) => getDayCellProps(date, month)}
                        defaultView={Views.MONTH}
                        draggableAccessor={(event) => !(event as CalendarEvent).isBinding}
                        endAccessor="end"
                        events={calendarEvents || []}
                        localizer={localizer}
                        onDragStart={() => setIsDragging(true)}
                        onEventDrop={(event) => handleEventDrop(event as PendingCalendarChange)}
                        onEventResize={(event) => handleEventResize(event as PendingCalendarChange)}
                        popup={true}
                        resizableAccessor={(event) => !(event as CalendarEvent).isBinding}
                        showMultiDayTimes
                        startAccessor="start"
                        step={60}
                        views={[Views.MONTH]}
                      />
                    </CalendarStylesOverride>
                    {isLoadingMonths && (
                      <div style={{ height: '500px', position: 'relative' }}>
                        <SkeletonComponent height="100%" width="100%" />
                      </div>
                    )}
                  </MonthContainer>
                );
              })}
            </MonthsContainer>
          </ScrollableCalendarContainer>
        ) : (
          <>
            {isLoading ? (
              <SkeletonComponent height="100vh" width="100%" />
            ) : (
              <CalendarStylesOverride
                $hideDragPreview={
                  isDragging && draggingEventStartDay !== null && hoveredDateKey === draggingEventStartDay
                }
                data-visible-month={format(calendarFilters?.notBeforeUtc || new Date(), 'yyyy-MM-dd')}
                onPointerLeave={handleCalendarPointerLeave}
                onPointerMove={handleCalendarPointerMove}
                onPointerUp={() => {
                  setIsDragging(false);
                  setDraggingEventStartDay(null);
                }}
              >
                {/* @ts-expect-error react-big-calendar types not compatible with React 19 */}
                <DnDCalendar
                  components={{
                    event: CalendarAppointmentEvent,
                    dateHeader: CalendarDateHeader,
                    toolbar: CustomToolbar,

                    showMore: ({ count, remainingEvents, slotDate }) => (
                      <CustomShowMore
                        count={count}
                        handleEventClick={handleEventClick}
                        remainingEvents={remainingEvents as CalendarEvent[]}
                        slotDate={slotDate}
                      />
                    ),
                  }}
                  date={calendarFilters?.notBeforeUtc}
                  dayPropGetter={(date) =>
                    getDayCellProps(date, calendarFilters?.notBeforeUtc ? calendarFilters.notBeforeUtc : new Date())
                  }
                  defaultView={Views.MONTH}
                  draggableAccessor={(event) => !(event as CalendarEvent).isBinding}
                  endAccessor="end"
                  events={calendarEvents || []}
                  localizer={localizer}
                  onDragStart={(args) => {
                    setIsDragging(true);
                    setDraggingEventStartDay(format((args as { event: CalendarEvent }).event.start, 'yyyy-MM-dd'));
                  }}
                  onEventDrop={(event) => handleEventDrop(event as PendingCalendarChange)}
                  onEventResize={(event) => handleEventResize(event as PendingCalendarChange)}
                  popup={true}
                  resizableAccessor={(event) => !(event as CalendarEvent).isBinding}
                  showMultiDayTimes
                  startAccessor="start"
                  step={60}
                  views={[Views.MONTH]}
                />
              </CalendarStylesOverride>
            )}
          </>
        )}
      </DesktopOnly>

      <AppointmentDrawer
        appointment={selectedAppointment}
        initialFormState={appointmentFormPrefill}
        isLoadingAppointment={isLoadingAppointment}
        isOpen={!!isCreating || !!isEditing}
        notFoundMessage={drawerErrorMessage}
        onClose={() => {
          setDrawerErrorMessage(undefined);
          close();
        }}
        onDelete={(id) => {
          setSelectedAppointment(null);

          if (!id) {
            return;
          }

          if (calendarEvents) {
            const itemsAux = [...calendarEvents];
            const idxToDelete = itemsAux.findIndex((item) => item.id === id);

            if (idxToDelete) {
              itemsAux.splice(idxToDelete, 1);
              setCalendarEvents(itemsAux);
            }
          }
        }}
        onSave={(item) => {
          setSelectedAppointment(null);
          const calendarEvent = item?.calendarEvent;

          if (!item || !calendarEvent) {
            return;
          }

          setCalendarEvents((prevEvents) => {
            if (!prevEvents) {
              return [calendarEvent];
            }

            const existingEventIndex = prevEvents.findIndex((event) => event.id === calendarEvent?.id);

            if (existingEventIndex !== -1) {
              const updatedEvents = [...prevEvents];

              updatedEvents[existingEventIndex] = calendarEvent;

              return updatedEvents;
            }

            return [...prevEvents, calendarEvent];
          });
          setToastMessage(
            t(`pm:snackbarCalendar.${item.isCreated ? 'messageSuccessfulCreated' : 'messageSuccessfulUpdated'}`),
          );
          setShouldShowToast(true);
          setAppointmentFormPrefill(undefined);
        }}
        ref={drawerRef}
      />
    </CalendarContainer>
  );
};
